import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Image } from './Image'
import { Member, MemberConstructorArgs } from './Member'
import { FKType } from './SQLBuilder'
import { User } from './User'

type TeamConstructorArgs = Omit<ConstructorArgs<() => Team>, 'members'> & {
    members: Member[] | MemberConstructorArgs[] | null
}

export class Team extends BaseRecord {
    readonly recordType = 'team'

    name?: string
    image?: Image
    bcc?: boolean
    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment
    members: Member[] = []

    static GROUP_CAPACITY = 25

    constructor(data?: Partial<TeamConstructorArgs>, params?: { members?: User[] }) {
        super(data)
        if (!data) {
            this.creator = PSApplication.me()
            this.owner = this.creator.owner
            this.bcc = true
            if (params?.members) {
                for (let member of params.members) {
                    this.members.push(new Member(undefined, { team: this, person: member }))
                }
            }
            return
        }

        this.name = data.name ?? undefined
        this.image = this.propertyFromImageArg(data.image, data.image_uri)
        this.members = (data.members ?? [])
            .map((data) => (data instanceof Member ? data : new Member(data)))
            .filter((member) => !member.cancelledDate)
        this.bcc = data.bcc ?? false
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
    }

    clone(overrides?: Partial<TeamConstructorArgs>): Team {
        return new Team({ ...this.serializeForClone(), ...overrides })
    }

    toString(): string {
        const members = this.members
        let string: string
        if (members && members.length === 1) string = '1 member'
        else string = `${members ? members.length : 0} members`
        return string
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.image) this.image = BaseRecord.fromSerializer(data.image, Image)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
        if (data.members)
            this.members = data.members.map((member: any) =>
                BaseRecord.fromSerializer(data._member, Member)
            )
    }

    static fk: FKType = {
        users: ['owner', 'creator', 'members'],
        images: ['image'],
        columnExtractors: {
            members: 'person',
        },
    }

    static dataType(): DataType {
        return 'teams'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['name'] = this.name
        dict['bcc'] = this.bcc
        dict['image'] = this.image?.id ?? ''
        if (!this.id) {
            const list: string[] = []
            for (let member of this.members) if (member.person?.id) list.push(member.person.id)
            dict['members'] = list
        }
        return dict
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
}
